import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import * as React from "react";

import { Button, type IButtonProps } from "@krea/shared/design-system/Button";
import { Input } from "@krea/shared/design-system/input/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@krea/shared/design-system/modal/sheet";
import { Separator } from "@krea/shared/design-system/Separator";
import { Skeleton } from "@krea/shared/design-system/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@krea/shared/design-system/Tooltip";
import { cn } from "@krea/shared/utils";

import { clampWidth, nextWidthFromDrag, parseRem } from "./sidebar-resize";

function useMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    function windowResize() {
      // Mobile cutoff is ≤1199px to match the app-wide useMobile hook
      // (@krea/shared/hooks/useMobile) and the `xl` breakpoint, which is
      // min-width:1200 (desktop at ≥1200). Keep these in sync.
      if (window.matchMedia("(max-width: 1199px)").matches) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }

    windowResize();

    window.addEventListener("resize", windowResize);

    return () => {
      window.removeEventListener("resize", windowResize);
    };
  }, []);

  return isMobile;
}

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

interface SidebarContextProps {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  // Resize additions (opt-in; inert unless `resizable` is set on the provider).
  width: string;
  setWidth: (width: string) => void;
  resizable: boolean;
  minWidth: string;
  maxWidth: string;
  defaultWidth: string;
  isResizing: boolean;
  setIsResizing: (value: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SidebarProvider = ({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  resizable = false,
  defaultWidth = SIDEBAR_WIDTH,
  minWidth = SIDEBAR_WIDTH,
  maxWidth = SIDEBAR_WIDTH,
  width: widthProp,
  onWidthChange,
  widthStorageKey,
  className,
  style,
  children,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  resizable?: boolean;
  defaultWidth?: string;
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  onWidthChange?: (width: string) => void;
  widthStorageKey?: string;
} & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const isMobile = useMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;

      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((open) => !open);
    } else {
      setOpen((open) => !open);
    }
  }, [isMobile, setOpen]);

  // --- Resizable width state (opt-in) ---
  const [isResizing, setIsResizing] = React.useState(false);

  const readInitialWidth = React.useCallback(() => {
    if (widthStorageKey && typeof window !== "undefined") {
      try {
        const stored = window.localStorage.getItem(widthStorageKey);
        if (stored) return clampWidth(stored, minWidth, maxWidth);
      } catch {
        // localStorage can throw (Safari private mode, quota, blocked by
        // policy); fall through to the clamped default rather than crash.
      }
    }
    return clampWidth(defaultWidth, minWidth, maxWidth);
  }, [widthStorageKey, minWidth, maxWidth, defaultWidth]);

  const [_width, _setWidth] = React.useState(readInitialWidth);
  const width = widthProp ?? _width;

  const setWidth = React.useCallback(
    (value: string) => {
      const clamped = clampWidth(value, minWidth, maxWidth);
      if (onWidthChange) {
        onWidthChange(clamped);
      } else {
        _setWidth(clamped);
        if (widthStorageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(widthStorageKey, clamped);
          } catch {
            // Persisting the width is best-effort; ignore storage failures.
          }
        }
      }
    },
    [onWidthChange, widthStorageKey, minWidth, maxWidth],
  );

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        const target = event.target as HTMLElement | null;
        // Don't hijack the shortcut while the user is typing in a field or
        // rich-text editor (Cmd/Ctrl+B = bold there).
        if (
          target?.isContentEditable ||
          target?.tagName === "INPUT" ||
          target?.tagName === "TEXTAREA"
        ) {
          return;
        }
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      width,
      setWidth,
      resizable,
      minWidth,
      maxWidth,
      defaultWidth,
      isResizing,
      setIsResizing,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      toggleSidebar,
      width,
      setWidth,
      resizable,
      minWidth,
      maxWidth,
      defaultWidth,
      isResizing,
    ],
  );

  return (
    <SidebarContext value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={
            {
              "--sidebar-width": width,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "group/sidebar-wrapper flex h-svh w-full has-[[data-variant=inset]]:bg-gray-100 overflow-y-hidden",
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext>
  );
};
SidebarProvider.displayName = "SidebarProvider";

const Sidebar =
  /**
   * z-index for the mobile Sheet (overlay + panel). Opt-in: when unset the
   * Sheet keeps its default z-50. Consumers whose app header out-stacks z-50
   * (e.g. customer/partner, header z-10001) pass a higher value so the mobile
   * drawer covers the header instead of rendering beneath it.
   */
  ({
    side = "left",
    variant = "sidebar",
    collapsible = "offcanvas",
    offset = false,
    mobileZIndex,
    className,
    children,
    ref,
    ...props
  }: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
    offset?: boolean;
    /**
     * z-index for the mobile Sheet (overlay + panel). Opt-in: when unset the
     * Sheet keeps its default z-50. Consumers whose app header out-stacks z-50
     * (e.g. customer/partner, header z-10001) pass a higher value so the mobile
     * drawer covers the header instead of rendering beneath it.
     */
    mobileZIndex?: number;
  } & {
    ref?: React.Ref<HTMLDivElement>;
  }) => {
    const { isMobile, state, openMobile, setOpenMobile, isResizing } =
      useSidebar();
    const gapTransition = isResizing
      ? ""
      : "transition-[width] duration-200 ease-linear";
    const panelTransition = isResizing
      ? ""
      : "transition-[left,right,width] duration-200 ease-linear";
    // Offset consumers (customer/partner) drive the panel's `top` AND
    // `height` via --sidebar-top/--sidebar-height as the header hides on
    // scroll (height = 100vh - top, so the panel's bottom edge stays pinned
    // to the viewport). Both must glide together at the content pane's
    // `top 0.4s ease` — height was previously left off this list, so it
    // snapped instantly while top eased, opening a momentary gap (or
    // overflow) at the bottom. Omitted while resizing so drags track the
    // pointer.
    const offsetPanelTransition: React.CSSProperties | undefined =
      offset && !isResizing
        ? {
            transition:
              "left 200ms linear, right 200ms linear, width 200ms linear, top 0.4s ease, height 0.4s ease",
          }
        : undefined;

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "flex h-full w-(--sidebar-width) flex-col bg-gray-100 text-white",
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            zIndex={mobileZIndex}
            className="w-(--sidebar-width) bg-gray-100 p-0 text-black [&>button]:hidden"
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer hidden text-black md:block"
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "relative w-(--sidebar-width) bg-transparent",
            gapTransition,
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
              : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
          )}
        />
        <div
          className={cn(
            offset
              ? "fixed top-(--sidebar-top) z-10 hidden h-(--sidebar-height) w-(--sidebar-width) md:flex"
              : "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) md:flex",
            offset ? "" : panelTransition,
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:-left-(--sidebar-width)"
              : "right-0 group-data-[collapsible=offcanvas]:-right-(--sidebar-width)",
            // Adjust the padding for floating and inset variants.
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
              : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
            className,
          )}
          {...props}
          style={
            offset ? { ...props.style, ...offsetPanelTransition } : props.style
          }
        >
          <div
            data-sidebar="sidebar"
            className="flex  h-full w-full flex-col bg-gray-100 group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-gray-200 group-data-[variant=floating]:shadow-sm"
          >
            {children}
          </div>
        </div>
      </div>
    );
  };
Sidebar.displayName = "Sidebar";

const SidebarTrigger = ({
  className,
  onClick,
  ref,
  ...props
}: IButtonProps & {
  ref?: React.Ref<React.ElementRef<typeof Button>>;
}) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"button"> & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "cursor-pointer absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-gray-200  group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-gray-100",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...props}
    />
  );
};
SidebarRail.displayName = "SidebarRail";

const RESIZE_KEYBOARD_STEP_PX = 16;

const SidebarResizeHandle = ({
  className,
  side = "left",
  ref,
  ...props
}: React.ComponentProps<"button"> & { side?: "left" | "right" } & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const {
    resizable,
    isMobile,
    width,
    setWidth,
    minWidth,
    maxWidth,
    defaultWidth,
    setIsResizing,
  } = useSidebar();

  const startX = React.useRef(0);
  const startWidth = React.useRef(width);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    startX.current = event.clientX;
    startWidth.current = width;
    setIsResizing(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const delta = event.clientX - startX.current;
    setWidth(
      nextWidthFromDrag(startWidth.current, delta, side, minWidth, maxWidth),
    );
  };

  const endResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    setIsResizing(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const widen = side === "left" ? "ArrowRight" : "ArrowLeft";
    const narrow = side === "left" ? "ArrowLeft" : "ArrowRight";
    if (event.key === widen) {
      event.preventDefault();
      setWidth(
        nextWidthFromDrag(
          width,
          side === "left" ? RESIZE_KEYBOARD_STEP_PX : -RESIZE_KEYBOARD_STEP_PX,
          side,
          minWidth,
          maxWidth,
        ),
      );
    } else if (event.key === narrow) {
      event.preventDefault();
      setWidth(
        nextWidthFromDrag(
          width,
          side === "left" ? -RESIZE_KEYBOARD_STEP_PX : RESIZE_KEYBOARD_STEP_PX,
          side,
          minWidth,
          maxWidth,
        ),
      );
    }
  };

  if (!resizable || isMobile) return null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: interactive resize handle needs button semantics (keyboard focus + click handlers) alongside the separator role; no native element provides both
    <button
      ref={ref}
      type="button"
      data-sidebar="resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={parseRem(width)}
      aria-valuemin={parseRem(minWidth)}
      aria-valuemax={parseRem(maxWidth)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endResize}
      onPointerCancel={endResize}
      onLostPointerCapture={endResize}
      onKeyDown={handleKeyDown}
      onDoubleClick={() => setWidth(defaultWidth)}
      className={cn(
        "absolute inset-y-0 z-30 block w-1.5 cursor-col-resize touch-none select-none outline-hidden",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent hover:after:bg-primary focus-visible:after:bg-primary",
        side === "left" ? "right-0" : "left-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarResizeHandle.displayName = "SidebarResizeHandle";

const SidebarInset = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"main"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <main
      ref={ref}
      className={cn(
        "relative flex w-full flex-1 flex-col bg-white",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm",

        className,
      )}
      {...props}
    />
  );
};
SidebarInset.displayName = "SidebarInset";

const SidebarInput = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof Input> & {
  ref?: React.Ref<React.ElementRef<typeof Input>>;
}) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-white shadow-none focus-visible:ring-2 focus-visible:ring-primary",
        className,
      )}
      {...props}
    />
  );
};
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
};
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
};
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof Separator> & {
  ref?: React.Ref<React.ElementRef<typeof Separator>>;
}) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-gray-200 ", className)}
      {...props}
    />
  );
};
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  );
};
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = ({
  className,
  asChild = false,
  ref,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean } & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-gray-600 outline-hidden ring-primary transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = ({
  className,
  asChild = false,
  ref,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean } & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-black outline-hidden ring-primary transition-transform hover:bg-gray-200 hover:text-black  focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn("w-full text-sm", className)}
    {...props}
  />
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"ul"> & {
  ref?: React.Ref<HTMLUListElement>;
}) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-1", className)}
    {...props}
  />
);
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"li"> & {
  ref?: React.Ref<HTMLLIElement>;
}) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn("group/menu-item relative", className)}
    {...props}
  />
);
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
  "peer/menu-button group-has-data-[sidebar=menu-action]/menu-item:pr-8  flex  w-full cursor-pointer items-center  gap-2  overflow-hidden rounded-md p-2 text-left text-sm text-gray-600 outline-hidden ring-primary transition-[width,height,padding] hover:bg-gray-200 hover:text-black focus-visible:ring-2 disabled:pointer-events-none disabled:cursor-default disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:cursor-default aria-disabled:opacity-50 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-gray-200  hover:text-black",
        outline:
          "bg-white  shadow-sm hover:bg-gray-100 hover:text-black hover:shadow-md",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const SidebarMenuButton = ({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ref,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants> & {
    ref?: React.Ref<HTMLButtonElement>;
  }) => {
  const Comp = asChild ? Slot : "button";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      ref={ref}
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        sidebarMenuButtonVariants({ variant, size }),
        isActive && "bg-gray-200 font-semibold text-black",
        className,
      )}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
};
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = ({
  className,
  asChild = false,
  showOnHover = false,
  ref,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
} & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-black outline-hidden ring-primary transition-transform hover:bg-gray-200 hover:text-black  focus-visible:ring-2 peer-hover/menu-button:text-black  [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-black  md:opacity-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      "pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-white",
      "peer-hover/menu-button:text-black  peer-data-[active=true]/menu-button:text-black",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className,
    )}
    {...props}
  />
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = ({
  className,
  showIcon = false,
  ref,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
} & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
};
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"ul"> & {
  ref?: React.Ref<HTMLUListElement>;
}) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-gray-200  px-2.5 py-0.5",
      // Collapsed-to-icon: every clickable sub-item (e.g. "New application", "Create lead")
      // stays reachable as its own icon button instead of disappearing — drop the indent/border
      // that only made sense next to visible labels, and center the icon column.
      "group-data-[collapsible=icon]:mx-0 group-data-[collapsible=icon]:translate-x-0 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:border-l-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0",
      className,
    )}
    {...props}
  />
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = ({
  ref,
  ...props
}: React.ComponentProps<"li"> & {
  ref?: React.Ref<HTMLLIElement>;
}) => <li ref={ref} {...props} />;
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = ({
  asChild = false,
  size = "md",
  isActive,
  tooltip,
  className,
  ref,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & {
  ref?: React.Ref<HTMLAnchorElement>;
}) => {
  const Comp = asChild ? Slot : "a";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        // No `[&>svg]:text-gray-600`. It pinned the icon to gray while the button's own text
        // moves — black when active or hovered — so an active sub-item rendered a black label
        // beside a gray icon. Letting the icon inherit currentColor is also what
        // SidebarMenuButton (the parent-level equivalent) already does; this was the only
        // sub-button overriding it.
        "flex h-7 min-w-0 -translate-x-px cursor-pointer items-center gap-2 overflow-hidden rounded-md px-2 text-gray-600 outline-hidden ring-primary hover:bg-gray-200 hover:text-black focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        isActive && "bg-gray-200 font-semibold text-black",
        // Collapsed-to-icon: a sub-item WITH an icon shrinks to the same square icon button the
        // top-level SidebarMenuButton uses instead of disappearing — overflow-hidden plus a
        // content box exactly icon-sized (size-8 minus p-2 padding = 16px = the icon) clips the
        // label with no extra hidden rule needed, and the tooltip below carries the label
        // instead. A sub-item with NO icon (e.g. lender's filter trees, all label-only) has
        // nothing to show in a 32px square — surfacing it would just be a row of unlabeled blank
        // buttons — so those stay hidden collapsed, same as before this change.
        "group-data-[collapsible=icon]:[&:not(:has(svg))]:hidden",
        "group-data-[collapsible=icon]:[&:has(svg)]:size-8 group-data-[collapsible=icon]:[&:has(svg)]:translate-x-0 group-data-[collapsible=icon]:[&:has(svg)]:justify-center group-data-[collapsible=icon]:[&:has(svg)]:p-2",
        className,
      )}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
};
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarResizeHandle,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
